import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuthStore, useStatusStore } from '@lib/store';
import { cn, RoutePaths } from '@shared/utils';
import { Logo, Logout, MessageIndicator } from '@shared/widgets';
import './header-animation.scss';

interface HeaderProps {
  className?: string;
  menu: { link: string; name: string }[];
}

export const Header = (props: HeaderProps) => {
  const location = useLocation();

  const authStore = useAuthStore();
  const statusStore = useStatusStore();

  const [cardholderVisible, setCardholderVisible] = useState<boolean>(false);

  const hideCardholderHandler = () => setTimeout(() => setCardholderVisible(false), 200);

  return (
    <header
      className={cn(
        'main-menu-animation',
        'fixed top-0 left-0 z-1000 h-20 w-full bg-[#131416] shadow-[12px_12px_24px_0_#0000003b,_inset_0_2px_0_0_#343434]',
        props.className,
      )}
    >
      <div className='mx-auto my-0 flex h-full w-full max-w-[1294px] items-center justify-start overflow-visible px-5'>
        <div className={cn('mr-20 flex h-full items-start justify-start', 'not-xl:mr-0')}>
          <div
            className={cn(
              'menu-logo-animation',
              'relative flex h-full w-auto flex-[0_0_80px] items-center justify-start pr-4',
              cardholderVisible && 'logo-swipe',
            )}
          >
            <div className={cn('menu-logo__inner', 'relative top-0.5 z-10 w-[120px]')}>
              <div className='relative'>
                <Logo />
              </div>
            </div>
          </div>
          <div
            className={cn('flex h-full w-auto flex-[0_0_295px] items-start justify-start', 'not-xl:shrink')}
            tabIndex={0}
            onBlur={hideCardholderHandler}
          >
            <div className='relative flex h-20 w-full items-start justify-center text-[#888] uppercase'>
              <div className='relative z-110 flex h-[inherit] flex-col justify-center p-1 px-1 pt-3 pb-1 select-none'>
                <div className={cn('cardholder__name-animation', 'relative flex items-center justify-start')}>
                  <div className={cn('text-[15px] font-bold text-[#f2f2f2]', 'not-xl:text-sm not-xl:font-semibold')}>{authStore.data?.name}</div>
                </div>
                <div className={cn('last-enter-animation mt-0.5 text-xs font-semibold text-[#888]', 'not-xl:text-[10px] not-xl:font-semibold')}>
                  <span className='mr-1'>Last login:</span>
                  <span>{dayjs(authStore.data?.last_login).format('ddd, DD.MM.YY [AT] hh:mm A')}</span>
                </div>
                <div className={cn('last-enter-animation mt-0.5 text-xs font-semibold text-[#888]', 'not-xl:text-[10px] not-xl:font-semibold')}>
                  <span className='mr-1'>Status:</span>
                  <span>{statusStore.data?.OperationalStatus}</span>
                </div>
              </div>
              <div className='absolute inset-0 z-[100] flex h-[150px] scale-0 items-center justify-center bg-gradient-to-b from-[#262626] to-[#272727] opacity-0 shadow-[12px_12px_40px_0_#0000003b,_inset_0_2px_0_0_#343434] transition-opacity duration-400 ease-out' />
            </div>
          </div>
        </div>
        <div className='flex w-full flex-[0_1_64%] items-center justify-between'>
          <div className='flex h-20 w-full items-start justify-around'>
            <Logout
              className={cn(
                'menu-item-animation',
                'relative flex h-20 w-[100px] cursor-pointer flex-col items-center justify-around border-l border-[#91919133] py-2',
              )}
            />
            {props.menu.map((item) => (
              <NavLink
                key={item.link}
                className={cn(
                  'menu-item-animation',
                  'relative z-10 flex h-full flex-1 cursor-pointer items-center justify-center overflow-hidden text-sm font-semibold whitespace-nowrap text-[#888] uppercase no-underline',
                  location.pathname === item.link && 'menu-item--current text-white',
                )}
                to={item.link}
              >
                <div className={cn('menu-text-animation', 'relative z-50 select-none')}>{item.name}</div>
              </NavLink>
            ))}
          </div>
          <NavLink to={RoutePaths.messages.onNavigate({ dir: 'inbox' })} className='relative -mr-4 block'>
            <MessageIndicator
              active={
                location.pathname.startsWith(RoutePaths.messages.onNavigate({ dir: 'inbox' })) ||
                location.pathname.startsWith(RoutePaths.messages.onNavigate({ dir: 'sent' }))
              }
            />
          </NavLink>
        </div>
      </div>
    </header>
  );
};
